const DEFAULT_ENVIRONMENT = 'qa';
const DEFAULT_VARIANT = 'default';
const DEFAULT_VERSION = '1.0.37';

const sharedConfig = {
  appName: 'Registro de Candidatos Web',
  captchaKey: '6LdJ9TcpAAAAAD2NlLy3kdh6VwhGbHBJZFPAixPB',
  cookieDomain: '.mystratis.com',
  cryptoKey: 'GjtHYIj+UNM0eyLVxuJYxvoedLc/Jz4Cieo4xU+vruQ=',
  cryptoIv: 'NZbMuA0Wqcsp6RjY6z7X6w==',
  version: process.env.REACT_APP_VERSION || DEFAULT_VERSION
};

const environmentConfig = {
  qa: {
    environment: 'qa',
    gatewayUrl: 'https://api-qa.mystratis.com/stratis-web-v1.0/'
  },
  uat: {
    environment: 'uat',
    gatewayUrl: 'https://api-uat.mystratis.com/stratis-web-v1.0/'
  },
  production: {
    environment: 'production',
    gatewayUrl: 'https://api.mystratis.com/stratis-web-v1.0/'
  }
};

const variantConfig = {
  default: {
    variant: 'default',
    careersLanding: {
      qa: 'https://rh.mystratis.com/',
      uat: 'https://careers-uat.mx.mystratis.com/',
      production: 'https://careers.mx.mystratis.com/'
    },
    cookieName: {
      qa: '_qa_jobBoardSession',
      uat: '_uat_jobBoardSession',
      production: '_jobBoardSession'
    },
    logoutCookieName: {
      qa: '_qa_jobBoardExternalLogOut',
      uat: '_uat_jobBoardExternalLogOut',
      production: '_jobBoardExternalLogOut'
    }
  },
  'siglo-cl': {
    variant: 'siglo-cl',
    careersLanding: {
      qa: 'https://qa-careers-siglo-bpo-cl.mystratis.com/',
      uat: 'https://qa-careers-siglo-bpo-cl.mystratis.com/',
      production: 'https://careers-siglo-bpo-cl.mystratis.com/'
    },
    cookieName: {
      qa: '_cl_qa_jobBoardSession',
      uat: '_cl_qa_jobBoardSession',
      production: '_cl_jobBoardSession'
    },
    logoutCookieName: {
      qa: '_cl_qa_jobBoardExternalLogOut',
      uat: '_cl_qa_jobBoardExternalLogOut',
      production: '_cl_jobBoardExternalLogOut'
    }
  }
};

const environmentAliases = {
  dev: 'qa',
  local: 'qa',
  development: 'qa',
  prod: 'production'
};

const variantAliases = {
  siglo: 'siglo-cl',
  'siglo-cl': 'siglo-cl',
  cl: 'siglo-cl'
};

const normalizeEnvironment = value => {
  const environment = String(value || '').trim().toLowerCase();
  return environmentAliases[environment] || environment;
};

const normalizeVariant = value => {
  const variant = String(value || '').trim().toLowerCase();
  return variantAliases[variant] || variant;
};

const getSearchParams = search => new URLSearchParams(search || '');

const getEnvironmentFromSearchParams = search => {
  const params = getSearchParams(search);
  return normalizeEnvironment(params.get('environment') || params.get('env'));
};

const getVariantFromSearchParams = search => {
  const params = getSearchParams(search);
  return normalizeVariant(params.get('variant') || params.get('brand') || params.get('client'));
};

const matchesHostnameToken = (hostname, token) => {
  const normalizedHostname = String(hostname || '').toLowerCase();

  return normalizedHostname === token ||
    normalizedHostname.includes(`-${token}`) ||
    normalizedHostname.includes(`${token}-`) ||
    normalizedHostname.includes(`.${token}`) ||
    normalizedHostname.includes(`${token}.`);
};

const getEnvironmentFromHostname = hostname => {
  const normalizedHostname = String(hostname || '').toLowerCase();

  if (!normalizedHostname ||
    normalizedHostname === 'localhost' ||
    normalizedHostname === '127.0.0.1' ||
    normalizedHostname === '0.0.0.0') {
    return DEFAULT_ENVIRONMENT;
  }

  if (matchesHostnameToken(normalizedHostname, 'uat')) {
    return 'uat';
  }

  if (matchesHostnameToken(normalizedHostname, 'qa')) {
    return 'qa';
  }

  return 'production';
};

const getVariantFromHostname = hostname => {
  const normalizedHostname = String(hostname || '').toLowerCase();

  if (normalizedHostname.includes('siglo')) {
    return 'siglo-cl';
  }

  return DEFAULT_VARIANT;
};

const getRuntimeEnvironment = () => {
  if (typeof window === 'undefined') {
    return {
      environment: normalizeEnvironment(process.env.REACT_APP_ENVIRONMENT) || DEFAULT_ENVIRONMENT,
      source: process.env.REACT_APP_ENVIRONMENT ? 'build-env' : 'default',
      rawValue: process.env.REACT_APP_ENVIRONMENT
    };
  }

  const environmentFromQueryParam = getEnvironmentFromSearchParams(window.location.search);

  if (environmentFromQueryParam) {
    return {
      environment: environmentFromQueryParam,
      source: 'query-param',
      rawValue: window.location.search
    };
  }

  return {
    environment: getEnvironmentFromHostname(window.location.hostname),
    source: 'hostname',
    rawValue: window.location.hostname
  };
};

const getRuntimeVariant = () => {
  if (typeof window === 'undefined') {
    return {
      variant: normalizeVariant(process.env.REACT_APP_VARIANT) || DEFAULT_VARIANT,
      source: process.env.REACT_APP_VARIANT ? 'build-env' : 'default',
      rawValue: process.env.REACT_APP_VARIANT
    };
  }

  const variantFromQueryParam = getVariantFromSearchParams(window.location.search);

  if (variantFromQueryParam) {
    return {
      variant: variantFromQueryParam,
      source: 'query-param',
      rawValue: window.location.search
    };
  }

  return {
    variant: getVariantFromHostname(window.location.hostname),
    source: 'hostname',
    rawValue: window.location.hostname
  };
};

const buildConfig = () => {
  const runtimeEnvironment = getRuntimeEnvironment();
  const runtimeVariant = getRuntimeVariant();
  const selectedEnvironment = environmentConfig[runtimeEnvironment.environment] || environmentConfig[DEFAULT_ENVIRONMENT];
  const selectedVariant = variantConfig[runtimeVariant.variant] || variantConfig[DEFAULT_VARIANT];
  const environment = selectedEnvironment.environment;

  return {
    ...sharedConfig,
    ...selectedEnvironment,
    variant: selectedVariant.variant,
    careersLanding: selectedVariant.careersLanding[environment],
    cookieName: selectedVariant.cookieName[environment],
    logoutCookieName: selectedVariant.logoutCookieName[environment],
    runtimeEnvironmentSource: runtimeEnvironment.source,
    runtimeEnvironmentRawValue: runtimeEnvironment.rawValue,
    runtimeEnvironmentResolved: environment,
    runtimeVariantSource: runtimeVariant.source,
    runtimeVariantRawValue: runtimeVariant.rawValue,
    runtimeVariantResolved: selectedVariant.variant
  };
};

export const appConfig = buildConfig();

export const loadRuntimeConfig = async () => {
  window.__APP_CONFIG__ = appConfig;
  return appConfig;
};

